import React, { useState, useEffect } from "react";
import { MdDeleteOutline, MdContentCopy } from "react-icons/md";
import OptionComponent from "./OptionComponent";

const QuestionComponent = ({
  question,
  depth,
  index,
  dragHandleProps,
  addOption,
  addChildQuestion,
  updateQuestion,
  updateOption,
  handleImageUpload,
  allCategories,
  addNewCategory,
  deleteQuestion,
  deleteOption,
  duplicateQuestion,
}) => {
  const [localText, setLocalText] = useState(question.text || "");
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    setLocalText(question.text || "");
  }, [question.text]);

  return (
    <div
      className={`mb-4 p-2 sm:p-3 md:p-4 border rounded ${
        depth % 2 === 0 ? "bg-gray-100" : "bg-gray-200"
      }`}
      style={{ marginLeft: `${depth * 10}px` }} // smaller indent for mobile
    >
      {/* Drag + question number */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-2 gap-2 md:gap-4">
        <div className="flex items-center gap-2">
          <div {...dragHandleProps} className="cursor-move bg-white p-1 rounded text-sm sm:text-base">
            ☰
          </div>
          <span className="font-semibold text-black text-sm sm:text-base">
            Q{index}
          </span>
        </div>

        <button
          className="text-xs sm:text-sm px-2 py-1 border rounded bg-gray-300 mt-2 md:mt-0"
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? "Expand" : "Collapse"}
        </button>
      </div>

      {/* Question input */}
      <input
        type="text"
        className="w-full p-2 sm:p-3 border rounded mb-2 text-black text-sm sm:text-base"
        placeholder="Question"
        value={localText}
        onChange={(e) => setLocalText(e.target.value)}
        onBlur={() => updateQuestion(question.id, "text", localText)}
      />

      {/* Image upload */}
      <input
        type="file"
        className="w-full p-2 sm:p-3 border rounded mb-2 text-black text-sm sm:text-base"
        accept="image/*"
        onChange={(e) => handleImageUpload(e, question.id)}
      />

      {question.image && (
        <img
          src={question.image}
          alt="Question Preview"
          className="mt-2 max-w-full sm:max-w-xs md:max-w-sm rounded"
        />
      )}

      {/* Category */}
      {depth === 0 ? (
        <div className="mb-2">
          <div className="flex flex-col sm:flex-row mb-2 gap-2">
            <select
              className="w-full sm:w-auto p-2 sm:p-3 border rounded text-black text-sm sm:text-base"
              value={question.category || ""}
              onChange={(e) =>
                updateQuestion(question.id, "category", e.target.value)
              }
            >
              <option value="">Select Category</option>
              {allCategories.map((cat, i) => (
                <option key={i} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            <button
              className="bg-blue-500 text-white px-2 sm:px-3 py-1 sm:py-2 rounded text-xs sm:text-sm"
              onClick={() => {
                const newCat = prompt("Enter new category name:");
                if (newCat?.trim()) {
                  addNewCategory(newCat.trim());
                  updateQuestion(question.id, "category", newCat.trim());
                }
              }}
            >
              + New
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm sm:text-base text-black mb-2">
          <strong>Category:</strong> {question.category || "Uncategorized"}
        </p>
      )}

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 mb-2">
        <button
          className="bg-green-500 text-white px-4 py-2 sm:px-5 sm:py-2 rounded text-sm sm:text-base w-full sm:w-auto"
          onClick={() => addOption(question.id)}
        >
          Add Option
        </button>

        <button
          className="bg-red-600 text-white px-4 py-2 sm:px-5 sm:py-2 rounded text-sm sm:text-base w-full sm:w-auto"
          onClick={() => deleteQuestion(question.id)}
        >
          <MdDeleteOutline />
        </button>

        <button
          className="bg-blue-500 text-white px-4 py-2 sm:px-5 sm:py-2 rounded text-sm sm:text-base w-full sm:w-auto"
          onClick={() => duplicateQuestion && duplicateQuestion(question.id)}
        >
          <MdContentCopy />
        </button>
      </div>

      {/* Options */}
      {!collapsed &&
        question.options.map((opt) => (
          <OptionComponent
            key={opt.id}
            questionId={question.id}
            option={opt}
            addChildQuestion={addChildQuestion}
            updateOption={updateOption}
            handleImageUpload={handleImageUpload}
            depth={depth}
            parentCategory={question.category}
            addOption={addOption}
            updateQuestion={updateQuestion}
            allCategories={allCategories}
            addNewCategory={addNewCategory}
            deleteQuestion={deleteQuestion}
            deleteOption={deleteOption}
            duplicateQuestion={duplicateQuestion}
          />
        ))}
    </div>
  );
};

export default React.memo(QuestionComponent);